import React from 'react'
import { Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import { FormFieldError, FormFieldLabel } from '../atoms/FormTextInput'
import { dependentSellContractPatch, SellContractFormData } from 'src/helpers/sellContractFormValidation'

export const sellContractFieldClass = 'self-stretch flex flex-col justify-start items-start gap-2'

export interface SellContractFieldMeta {
  error?: string
  touched?: boolean
  submitFailed?: boolean
}

export type ShowSellContractError = (meta: SellContractFieldMeta) => boolean

export interface SellContractFormControls {
  change: <K extends keyof SellContractFormData>(name: K, value: SellContractFormData[K]) => void
}

export const applySellContractPatch = (
  form: SellContractFormControls,
  values: SellContractFormData,
  patch: Partial<SellContractFormData>
): void => {
  const changes = dependentSellContractPatch(values, patch)
  const keys = Object.keys(changes) as Array<keyof SellContractFormData>
  keys.forEach((key) => {
    const value = changes[key]
    if (typeof value === 'undefined') {
      return
    }
    form.change(key, value)
  })
}

interface SegmentOption {
  value: string
  label: string
}

interface SellContractSegmentFieldProps {
  name: keyof SellContractFormData
  label: string
  options: SegmentOption[]
  values: SellContractFormData
  form: SellContractFormControls
  showError: ShowSellContractError
  className?: string
  grow?: boolean
  fallback?: string
  required?: boolean
}

const SellContractSegmentField = ({
  name,
  label,
  options,
  values,
  form,
  showError,
  className = 'w-80',
  grow = true,
  fallback = '',
  required = false
}: SellContractSegmentFieldProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Field name={name}>
      {({ input, meta }) => (
        <div className={sellContractFieldClass}>
          <FormFieldLabel label={label} />
          <InsetSegmentedControl
            className={className}
            grow={grow}
            value={typeof input.value === 'string' && input.value.length > 0 ? input.value : fallback}
            onChange={(nextValue: string) => {
              input.onChange(nextValue)
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              const patch = { [name]: nextValue } as Partial<SellContractFormData>
              applySellContractPatch(form, values, patch)
            }}
            options={options}
          />
          {required ? <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} /> : null}
        </div>
      )}
    </Field>
  )
}

export default SellContractSegmentField
